import { EnhancedErrorLogger } from '@/utils/error-tracking/enhanced-logger';

// Retry mechanism for failed chunk loads

// A chunk 404 after a deploy is PERMANENT: asset filenames are content-hashed,
// so the old name is gone from the CDN and refetching it can never succeed.
// So retry ONCE (covers a genuine transient network blip), then CONFIRM whether
// the asset is really gone (isChunkGone) before doing anything destructive.
const MAX_RETRY_COUNT = 1;
const RETRY_DELAY = 600;

/** Marker so a failed self-heal degrades to the manual screen instead of looping. */
const SELF_HEAL_KEY = 'chunk-self-heal-at';
const SELF_HEAL_COOLDOWN_MS = 60_000;

interface RetryState {
  count: number;
  lastAttempt: number;
}

const retryStates = new Map<string, RetryState>();

export function isChunkLoadError(error: unknown): boolean {
  if (!(error instanceof Error)) return false;
  return (
    error.message.includes('Failed to fetch dynamically imported module') ||
    error.message.includes('Loading chunk') ||
    error.message.includes('ChunkLoadError') ||
    error.message.includes('error loading dynamically imported module') ||
    error.message.includes('Importing a module script failed') ||
    error.message.includes('Failed to import')
  );
}

/**
 * Recover from a stale build: drop caches, drop the service worker, reload.
 *
 * The page keeps running the previous build's entry bundle until something
 * forces a reload, so the first lazily-loaded route after a deploy 404s. Idle
 * logout hits this every time because it navigates to /auth, a route most users
 * have not already fetched.
 *
 * Returns false when it declines (already tried recently, or no window), so the
 * caller can fall through to the visible error boundary rather than loop.
 */
/**
 * Is there visible work on screen that an unannounced reload would destroy?
 *
 * Self-healing ends in window.location.reload(). On /auth that is free —
 * nothing is on screen to lose — but the same path now runs from lazily-loaded
 * tabs inside an authenticated dashboard, where a deploy landing while an edit
 * dialog is open would silently discard whatever the user had typed.
 *
 * Two signals, because neither alone is right.
 *
 * An open modal covers the admin editing surfaces (student edits, transfers,
 * end dates, bulk actions). But `role="dialog"` on its own is too broad: Radix
 * gives PopoverContent that same role, so every filter menu and typeahead in
 * the app would count as unsaved work and quietly switch off the automatic
 * recovery this exists to provide. Popover content is rendered inside a popper
 * wrapper and Dialog never uses the popper, so excluding it separates the two
 * exactly.
 *
 * And a modal alone is too narrow: the family-facing application edit form at
 * /edit/:confirmationCode is a full page of plain inputs with no dialog
 * anywhere, so a reload there would discard a parent's typed answers — the
 * very loss this guard was added to prevent. Surfaces like that mark
 * themselves with data-unsaved-work once the user has actually changed
 * something.
 *
 * When this returns true the import failure falls through to the error
 * boundary, which offers the reload as a button. The user can save, then take
 * it.
 */
export function hasWorkInProgress(
  doc: Document | undefined = typeof document === 'undefined' ? undefined : document,
): boolean {
  if (!doc) return false;
  try {
    // Declared explicitly by editing surfaces that have no dialog to detect.
    if (doc.querySelector('[data-unsaved-work="true"]')) return true;

    return Array.from(doc.querySelectorAll('[role="dialog"], [role="alertdialog"]')).some(
      (el) => !el.closest('[data-radix-popper-content-wrapper]'),
    );
  } catch {
    return false;
  }
}

/**
 * May the service worker's "a new build is ready" reload go ahead right now?
 *
 * This is the reload that fires on every deploy for every open tab, so it is
 * the one with the most to destroy — and it used to decide on visibility
 * alone. A hidden tab is not an idle one: a parent filling in an application
 * switches away to look up a date of birth or a MARSS code, and on a phone
 * merely locking the screen hides the page. Reloading then returns them to an
 * empty form.
 *
 * Answering false does not cancel the update; the caller stays armed and asks
 * again the next time the tab is hidden, by which point the work is usually
 * saved.
 */
export function canReloadForNewBuild(
  doc: Document | undefined = typeof document === 'undefined' ? undefined : document,
): boolean {
  if (!doc) return false;
  if (!doc.hidden) return false;
  return !hasWorkInProgress(doc);
}

export async function selfHealStaleBuild(
  reload: () => void = () => window.location.reload(),
  doc: Document | undefined = typeof document === 'undefined' ? undefined : document,
): Promise<boolean> {
  if (typeof window === 'undefined') return false;

  // Declining here costs the user a click; reloading anyway could cost them
  // everything they had typed. Checked BEFORE the cooldown is stamped, so a
  // refusal does not spend the one self-heal this session is allowed.
  if (hasWorkInProgress(doc)) return false;

  // Read the budget now, but do not spend it yet. Stamping here meant a
  // refusal further down still burned the single self-heal this session is
  // allowed: the user closed their dialog, hit the same dead route again, and
  // got the error screen with no recovery left — the original bug, back again,
  // in exactly the case the guard had just fired for.
  try {
    const last = Number(sessionStorage.getItem(SELF_HEAL_KEY) || 0);
    if (last && Date.now() - last < SELF_HEAL_COOLDOWN_MS) return false;
  } catch {
    return false; // storage unavailable: don't risk a reload loop
  }

  try {
    if ('caches' in window) {
      const names = await caches.keys();
      await Promise.all(names.map((n) => caches.delete(n)));
    }
  } catch { /* best effort */ }

  try {
    if ('serviceWorker' in navigator) {
      const regs = await navigator.serviceWorker.getRegistrations();
      await Promise.all(regs.map((r) => r.unregister()));
    }
  } catch { /* best effort */ }

  // Check again. Dropping the caches and unregistering the service worker is
  // awaited work, and a dialog can open or a first keystroke can land while it
  // runs — the earlier answer is stale by now, and it is the reload, not the
  // teardown, that costs the user anything.
  if (hasWorkInProgress(doc)) return false;

  // Spend the budget only now that the reload is actually going ahead.
  try {
    sessionStorage.setItem(SELF_HEAL_KEY, String(Date.now()));
  } catch {
    return false;
  }

  reload();
  return true;
}


/** URL of the module a chunk error refers to, if the browser included it. */
export function chunkUrlFromError(error: unknown): string | null {
  if (!(error instanceof Error)) return null;
  return error.message.match(/https?:\/\/[^\s)'"]+\.js/)?.[0] ?? null;
}

/**
 * Is the chunk genuinely gone, or did the network just blink?
 *
 * Both cases surface as "Failed to fetch dynamically imported module", but the
 * right responses are opposite: a deploy needs a reload, a blip needs patience.
 * Guessing wrong is destructive — self-healing drops every cache and the
 * service worker — so confirm with a cheap HEAD before treating it as a deploy.
 * Anything inconclusive is treated as transient, i.e. the user keeps control.
 */
export async function isChunkGone(
  url: string | null,
  fetchImpl: typeof fetch = fetch,
  doc: Document | undefined = typeof document === 'undefined' ? undefined : document,
): Promise<boolean> {
  if (typeof navigator !== 'undefined' && navigator.onLine === false) return false;

  // Only Chromium puts the module URL in the message. Safari says "Importing a
  // module script failed." and Firefox "error loading dynamically imported
  // module" — no URL at all. Without a fallback the discriminator would return
  // false there and leave those users on the error screen: the reported bug,
  // unfixed, on a large share of a parent-facing portal.
  //
  // So probe THIS PAGE'S OWN entry bundle instead. If that 404s, the build it
  // came from has been replaced wholesale and every chunk it names is gone —
  // which is the same conclusion, reached without needing the failed URL.
  const target = url ?? getOwnEntryUrl(doc);
  if (!target) return false;

  try {
    const res = await fetchImpl(target, { method: 'HEAD', cache: 'no-store' });
    return res.status === 404 || res.status === 403;
  } catch {
    return false; // network-level failure: transient, not a deploy
  }
}

/**
 * The entry bundle this document loaded, read off its own script tag.
 *
 * This is a probe TARGET, not a comparison: nothing is diffed against served
 * markup, so none of the races that come with parsing index.html apply.
 */
export function getOwnEntryUrl(doc: Document | undefined): string | null {
  if (!doc) return null;
  const scripts = Array.from(doc.querySelectorAll('script[src]')) as HTMLScriptElement[];
  for (const el of scripts) {
    const src = el.getAttribute('src') || '';
    if (/assets\/index-[A-Za-z0-9_-]+\.js/.test(src)) return src;
  }
  return null;
}

export async function retryDynamicImport<T>(
  importFn: () => Promise<T>,
  chunkName?: string
): Promise<T> {
  const key = chunkName || importFn.toString();
  const state = retryStates.get(key) || { count: 0, lastAttempt: 0 };

  try {
    const result = await importFn();
    // Reset retry count on success
    retryStates.delete(key);
    return result;
  } catch (error) {
    const now = Date.now();
    const timeSinceLastAttempt = now - state.lastAttempt;

    const isChunkError = isChunkLoadError(error);

    if (isChunkError && state.count < MAX_RETRY_COUNT && timeSinceLastAttempt > RETRY_DELAY) {
      state.count++;
      state.lastAttempt = now;
      retryStates.set(key, state);

      console.warn(`Chunk load failed, retrying (${state.count}/${MAX_RETRY_COUNT})...`, error);

      // Wait before retrying
      await new Promise(resolve => setTimeout(resolve, RETRY_DELAY * state.count));

      // Clear module cache if possible
      if ('webpackChunkName' in error) {
        try {
          // Try to clear the failed chunk from cache
          const chunkUrl = (error as any).request || (error as any).url;
          if (chunkUrl && 'caches' in window) {
            const cacheNames = await caches.keys();
            for (const cacheName of cacheNames) {
              const cache = await caches.open(cacheName);
              await cache.delete(chunkUrl);
            }
          }
        } catch (cacheError) {
          console.warn('Failed to clear cache:', cacheError);
          EnhancedErrorLogger.getInstance().logQuickError(cacheError instanceof Error ? cacheError : new Error(String(cacheError)), 'ChunkRetry', { context: 'clearChunkCache' });
        }
      }

      // Retry the import
      return retryDynamicImport(importFn, chunkName);
    }

    // Retries exhausted. Reload only once we have CONFIRMED the asset is gone,
    // so a network blip never costs the user their caches. selfHealStaleBuild
    // declines if it already ran, so a genuinely broken deploy still surfaces
    // the error boundary instead of reloading forever.
    if (isChunkError && (await isChunkGone(chunkUrlFromError(error)))) {
      const healing = await selfHealStaleBuild();
      if (healing) {
        // Reload is in flight. Park briefly so nothing renders a torn state,
        // but never hang forever: if the reload is deferred or blocked, fall
        // through to the error boundary rather than leaving a dead spinner.
        await new Promise((resolve) => setTimeout(resolve, 10_000));
      }
    }

    // Release the retry budget for this chunk. Otherwise the exhausted count
    // sticks for the tab's lifetime and the NEXT attempt gets zero retries and
    // goes straight to a destructive self-heal.
    retryStates.delete(key);
    throw error;
  }
}

// Version check utilities
let currentVersion: string | null = null;

export function setAppVersion(version: string) {
  currentVersion = version;
  sessionStorage.setItem('app-version', version);
}

export function getAppVersion(): string | null {
  return currentVersion || sessionStorage.getItem('app-version');
}

export async function checkForNewVersion(): Promise<boolean> {
  try {
    // Fetch the index.html to check for new version
    const response = await fetch('/', {
      method: 'HEAD',
      cache: 'no-cache'
    });

    const etag = response.headers.get('etag');
    const lastModified = response.headers.get('last-modified');

    const currentEtag = sessionStorage.getItem('app-etag');
    const currentLastModified = sessionStorage.getItem('app-last-modified');

    // If we don't have stored values yet, this is the first check
    // Store the current values and DON'T report as new version
    if (!currentEtag && !currentLastModified) {
      if (etag) sessionStorage.setItem('app-etag', etag);
      if (lastModified) sessionStorage.setItem('app-last-modified', lastModified);
      return false; // First check - not a new version, just initializing
    }

    // Only report new version if we HAD previous values AND they changed
    if (etag && currentEtag && etag !== currentEtag) {
      sessionStorage.setItem('app-etag', etag);
      return true;
    }

    if (lastModified && currentLastModified && lastModified !== currentLastModified) {
      sessionStorage.setItem('app-last-modified', lastModified);
      return true;
    }

    return false;
  } catch (error) {
    console.error('Failed to check for new version:', error);
    EnhancedErrorLogger.getInstance().logQuickError(error instanceof Error ? error : new Error(String(error)), 'ChunkRetry', { context: 'checkForNewVersion' });
    return false;
  }
}

// Version update notification (no auto-reload to prevent tab state loss)
let isCheckingVersion = false;
let lastVersionCheck = 0;
let updateNotificationShown = false;
const VERSION_CHECK_COOLDOWN = 5 * 60 * 1000; // Only check once every 5 minutes

export function enableAutoReloadOnUpdate() {
  document.addEventListener('visibilitychange', async () => {
    const now = Date.now();

    // Don't check if:
    // - Page is hidden
    // - Already checking
    // - Checked recently (within cooldown period)
    // - Already showed an update notification this session
    if (
      document.hidden ||
      isCheckingVersion ||
      (now - lastVersionCheck) < VERSION_CHECK_COOLDOWN ||
      updateNotificationShown
    ) {
      return;
    }

    isCheckingVersion = true;
    lastVersionCheck = now;

    // Version check disabled - no update notification shown
    isCheckingVersion = false;
  });
}
